import React, { useState, useEffect, useCallback } from 'react';
import { Coordinates, Qibla } from 'adhan';
import { Box, Typography, Card, CardContent, Button } from '@mui/material';
import { isIOS } from 'react-device-detect';

interface QeblaDirectionProps {
  latitude: number | null;
  longitude: number | null;
  t: {
    qiblaDirection: string;
    latitude: string;
    longitude: string;
    qiblaAngle: string;
    deviceHeading: string;
    na: string;
    pointDeviceToQibla: string;
    enableCompass: string;
    gettingLocation: string;
    calculatingQibla: string;
    error: string;
  };
}

const QeblaDirection: React.FC<QeblaDirectionProps> = ({ latitude, longitude, t }) => {
  const [qeblaAngle, setQeblaAngle] = useState<number | null>(null);
  const [compassHeading, setCompassHeading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [orientationPermission, setOrientationPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');

  const handlePermission = async () => {
    if (isIOS && typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      try {
        const permissionState = await (DeviceOrientationEvent as any).requestPermission();
        if (permissionState === 'granted') {
            setOrientationPermission('granted');
        } else {
            setOrientationPermission('denied');
            setError("Permission not granted for device orientation.");
        }
      } catch (err) {
        console.error(err);
        setError("Error requesting device orientation permission.");
      }
    } else {
        setOrientationPermission('granted');
    }
  };

  useEffect(() => {
    if (latitude !== null && longitude !== null) {
      const coordinates = new Coordinates(latitude, longitude);
      const qiblaDirection = Qibla(coordinates);
      setQeblaAngle(qiblaDirection);
    }
  }, [latitude, longitude]);

  const handleOrientation = useCallback((event: any) => {
    let heading: number | null = null;
    if (event.webkitCompassHeading !== undefined && event.webkitCompassHeading !== null) {
      heading = event.webkitCompassHeading;
    } else if (event.alpha != null) {
      const alphaHeading: number = event.alpha;
      const screenAngle = window.screen?.orientation?.angle ?? 0;
      heading = ((alphaHeading - screenAngle) % 360 + 360) % 360;
    }
    setCompassHeading(heading);
  }, []);

  useEffect(() => {
    if (orientationPermission === 'granted') {
      window.addEventListener('deviceorientation', handleOrientation);
      return () => {
        window.removeEventListener('deviceorientation', handleOrientation);
      };
    }
  }, [orientationPermission, handleOrientation]);


  const getRotationStyle = () => {
    if (compassHeading !== null && qeblaAngle !== null) {
      const rotation = qeblaAngle - compassHeading;
      return { transform: `rotate(${rotation}deg)` };
    }
    return {};
  };

  if (error) {
    return (
      <Card sx={{ my: { xs: 2, sm: 3 }, p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Typography color="error">{t.error}: {error}</Typography>
      </Card>
    );
  }

  if (latitude === null || longitude === null) {
    return (
      <Card sx={{ my: { xs: 2, sm: 3 }, p: { xs: 2, sm: 3 }, borderRadius: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t.gettingLocation}</Typography>
      </Card>
    );
  }

  if (qeblaAngle === null) {
    return (
      <Card sx={{ my: { xs: 2, sm: 3 }, p: { xs: 2, sm: 3 }, borderRadius: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t.calculatingQibla}</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        my: { xs: 2, sm: 3 },
        p: { xs: 2, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        borderRadius: 3,
        mb: 4,
      }}
    >
      <CardContent sx={{ width: '100%', textAlign: 'center', '&:last-child': { pb: 2 } }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
          {t.qiblaDirection}
        </Typography>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
          mb: 2,
          textAlign: 'start',
        }}>
          <Typography variant="body2" color="text.secondary">{t.latitude}:</Typography>
          <Typography variant="body2">{latitude?.toFixed(4)}</Typography>
          <Typography variant="body2" color="text.secondary">{t.longitude}:</Typography>
          <Typography variant="body2">{longitude?.toFixed(4)}</Typography>
          <Typography variant="body2" color="text.secondary">{t.qiblaAngle}:</Typography>
          <Typography variant="body2">{qeblaAngle?.toFixed(2)}°</Typography>
          <Typography variant="body2" color="text.secondary">{t.deviceHeading}:</Typography>
          <Typography variant="body2">{compassHeading !== null ? compassHeading.toFixed(2) + '°' : t.na}</Typography>
        </Box>

        <Box
          sx={{
            position: 'relative',
            width: { xs: 160, sm: 200 },
            height: { xs: 160, sm: 200 },
            border: '3px solid',
            borderColor: 'grey.300',
            borderRadius: '50%',
            mx: 'auto',
            my: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'grey.100',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 0,
              height: 0,
              borderLeft: { xs: '8px solid transparent', sm: '10px solid transparent' },
              borderRight: { xs: '8px solid transparent', sm: '10px solid transparent' },
              borderBottom: { xs: '60px solid #D32F2F', sm: '80px solid #D32F2F' },
              top: '12%',
              transformOrigin: 'bottom center',
              transition: 'transform 0.15s ease-out',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
              ...getRotationStyle(),
            }}
          />
          <Typography
            sx={{
              position: 'absolute',
              top: 6,
              fontWeight: 'bold',
              color: 'grey.600',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            N
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t.pointDeviceToQibla}
        </Typography>

        <Button
          variant="outlined"
          fullWidth
          onClick={handlePermission}
          sx={{ borderRadius: 3, py: 1.2 }}
        >
          {t.enableCompass}
        </Button>
      </CardContent>
    </Card>
  );
};

export default QeblaDirection;
